/* eslint-disable import/no-cycle */
import API from 'base/auth/constants';
import { CoreSliceActions } from 'base/core/slice';
import ENVIRONMENT from 'config';
import {
  clearAuthToken,
  getAuthToken, getRefreshToken, setAuthToken, setRefreshToken,
} from 'helpers/token';
import { IRestApiResponse } from 'interfaces';
import { isArray } from 'lodash';
import jwt_decode from 'jwt-decode';

let store: any;
export const injectStore = (_store: any) => {
  store = _store;
};
let enterpriseId: string;

export class ResponseError extends Error {
  public response: Response;

  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object} The parsed JSON from the request
 */
const parseJSON = async (response: Response) => {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  if (!response.ok) {
    try {
      return await response.clone().text().then((text) => JSON.parse(text));
    } catch (error) {
      return response;
    }
  }
  return response.json();
};

/**
 * use the refresh token to get the new tokens
 */
// eslint-disable-next-line consistent-return
const refreshToken = async () => {
  const currentRefreshToken = getRefreshToken();

  const fetchResponse = await fetch(`${ENVIRONMENT.BACKEND_API}${API.POST_REFRESH_TOKEN.path}`, {
    body: JSON.stringify({
      token: currentRefreshToken,
      email: (jwt_decode(getAuthToken() as string) as any).username,
    }),
    method: API.POST_REFRESH_TOKEN.method,
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (![200, 201].includes(fetchResponse.status)) {
    clearAuthToken();
    if (!window.location.pathname.includes('login')) {
      window.location.href = '/login';
    }
    return false;
  }
  const res = (await fetchResponse.json()) as IRestApiResponse;
  setAuthToken(res.data.idToken.jwtToken);
  setRefreshToken(res.data.refreshToken.token);

  if (window.location.pathname.includes('login')) {
    window.location.href = '/';
  }
};

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
const checkStatus = async (response: Response) => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  if ([401, 423].includes(response.status)) {
    const data: any = await parseJSON(response);
    if (data.errors && data.errors.userLoggedOut) {
      window.location.reload();
      return false;
    }
    return response;
  }

  const res = (await response.json()) as IRestApiResponse;
  const apiMessage = res.message ?? 'Something went wrong';
  store.dispatch(
    CoreSliceActions.setMessage({
      message: isArray(apiMessage) ? res.message.join(', ') : res.message,
      success: false,
    }),
  );
  const error = new ResponseError(response);
  error.response = response;
  throw error;
};

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export async function request(
  _metadata: any,
  data: any,
  isSecure: boolean = true,
  isFormData: boolean = false,
  url: string = ENVIRONMENT.BACKEND_API,
): Promise<any> {
  const metadata = { ..._metadata };
  const pathTokens = metadata.path.split(':');
  if (metadata.path.indexOf(':') !== 0) {
    pathTokens.shift();
  }
  pathTokens.forEach((token: string) => {
    metadata.path = metadata.path.replace(`:${token}`, `${data[token]}`);
  });

  const backEndUrl = `${url}${metadata.path}`;
  const formData = new FormData();
  if (isFormData) {
    Object.keys(data).forEach((key) => formData.append(key, data[key]));
  }

  // check if the token is expires
  const jwtToken = getAuthToken();
  if (jwtToken) {
    if ((jwt_decode(jwtToken as string) as any).exp < (Date.now() / 1000)) {
      await refreshToken();
      console.log('second request after jwt token expires');
      await request(_metadata, data, isSecure, isFormData, url);
    }
  }

  const coreState = store.getState()['feature/base/core'];
  if (store && coreState) {
    enterpriseId = coreState.enterprise.id;
  }

  const options: RequestInit = {
    method: metadata.method,
    mode: 'cors', // no-cors, cors, same-origin
    cache: 'no-cache',
    credentials: 'same-origin', // include, *same-origin, omit
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
      ...(isSecure && {
        Authorization: `Bearer ${getAuthToken() || ''}`,
      }),
      ...(!isFormData && {
        'Content-Type': 'application/json',
      }),
      'X-ENT-ID': `${enterpriseId}`,
      'x-platform-id': 'expert-hub',
    },
    redirect: 'follow', // manual, *follow, error
    referrer: 'no-referrer', // no-referrer, *client
    ...(['POST', 'PUT', 'PATCH', 'DELETE'].includes(metadata.method) && {
      body: isFormData ? formData : JSON.stringify(data),
    }),
  };
  const fetchResponse = await fetch(backEndUrl, options);
  const response = await checkStatus(fetchResponse);
  if (!response) {
    return false;
  }
  if (isSecure && response.status === 401) {
    await refreshToken();
    console.log('Second request after request 401');
    await request(_metadata, data, isSecure, isFormData, url);
  }

  if (response.status === 423) {
    console.log('User has deleted. logout from the system');
    clearAuthToken();
    if (!window.location.pathname.includes('login')) {
      window.location.href = '/login';
    }
  }
  return parseJSON(response);
}
